import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { handleAutoScroll } from '../../Helpers/autoScrollWidgetRepositioning';
import { getPageNumbersWithVisibleSupplierMatches } from '../../Helpers/components';
import { exposeEventToPartner } from '../../Helpers/events';
import { getErrorMessage } from '../../Helpers/validation';
import { getGrowOnlyClampedHeight } from '../../Helpers/growOnlyHeight';
import holdWidgetHeightDuringFlagRefetch from '../../Helpers/flagRefetchHeightHold';
import {
  updateRecalculateRequiredWidgetHeight,
  updateRequiredWidgetHeight,
} from '../../Lib/redux/slices/dynamicHeight';
import {
  setHandleSideEffectsOfLegalStatement,
  setSideEffectDetailsForLegalStatement,
  updateHasStatementGraduallyDisappeared,
  updateIsLegalStatementRevealed,
  updateShouldRenderLegalStatement,
} from '../../Lib/redux/slices/legalStatement';
import {
  setShouldNavigateBackward,
  setShouldNavigateForward,
} from '../../Lib/redux/slices/navigationControls';
import { updateAndExposeDynamicHeightData } from '../../Utils/utils';
import {
  currentPageHasSelectAndConnectComponents,
  getLocaleForPrivacyPolicy,
} from '../LegalStatement/legalStatement.helpers';
import { getPageNumbersOfStaticLoaders } from '../Page/page.helpers';
import {
  determineRequiredHeightOfFormInputsSection,
  getWidgetHeightOverride,
  isPageChangeCompleted,
  reExamineRequiredWidgetHeight,
} from './dynamicWidgetHeight.helpers';
import baseFailsafeAlgorithmHeightData from '../../Data/Maps/dynamicWidgetHeight';
import SubmissionStatus from '../../Data/SubmissionStatus';
import { shouldPageTriggerPartialSubmission } from '../../Helpers/partialSubmission';
import logToConsole from '../../Utils/logToConsole';

const DynamicWidgetHeight = () => {
  /** NOTE: Why we use getBoundingClientRect().height & not clientHeight
   *  clientHeight obtains rounded integer values for the DOM element it refers to.
   *  getBoundingClientRect provides accurate floating point numbers for the dimensions.
   *  Rounding inaccuracies when reading the height values from the DOM can lead
   *  to the values carrying an error of upto +/-0.499...
   */
  const { currentPage, totalPages, isFinalPageBeforeSubmission } = useSelector(
    (store) => store.navigation
  );
  const { enterPhase, exitPhase } = useSelector((store) => store.transition);
  const { showCpcRecommendations, wasPreviousPageCpcCard } = useSelector(
    (store) => store.cpcCard
  );
  const isBounceNavigationAnimationEnabled = useSelector(
    (store) => store.features.isBounceNavigationAnimationEnabled
  );
  const { showAppointmentBooking, wasPreviousPageAppointmentBooking } =
    useSelector((store) => store.appointmentBooking);
  const {
    phoneNumberCountryCode,
    countryCode,
    languageCode,
    localeCode,
    submissionPageNumber,
  } = useSelector((i) => i.formConfig);
  const { partialSubmissionPages } = useSelector((store) => store.paging);
  const {
    requiresLegalStatementAboveNav:
      supplierMatchesRequireLegalStatementAboveNav,
  } = useSelector((store) => store.supplierMatches);
  const {
    statement,
    shouldRenderLegalStatement,
    isLegalStatementRevealed,
    handleSideEffectsOfLegalStatement,
    hasStatementGraduallyDisappeared,
    sideEffectDetailsForLegalStatement,
    isAwaitingFlagDrivenRefetch,
  } = useSelector((store) => store.legalStatement);

  const components = useSelector((store) => store.components);
  const supplierMatchPages =
    getPageNumbersWithVisibleSupplierMatches(components);
  const hasCurrentPageVisibleSupplierMatchQuestion =
    supplierMatchPages.includes(currentPage);

  const isSelectAndConnectPage = currentPageHasSelectAndConnectComponents(
    components,
    currentPage
  );

  const isThereALegalStatementOnTheCurrentPage =
    isFinalPageBeforeSubmission ||
    hasCurrentPageVisibleSupplierMatchQuestion ||
    shouldPageTriggerPartialSubmission(currentPage, partialSubmissionPages) ||
    isSelectAndConnectPage;
  /* TODO: Identify and replace all references to isPenultimatePage with
   *       isThereALegalStatementOnTheCurrentPage, once the future ticket
   *       to enable legal statements on any page is done and the webform
   *       settings have a mapping for this. Also, write a function to populate
   *       isThereALegalStatementOnTheCurrentPage accordingly.
   */
  const isSinglePageForm =
    typeof submissionPageNumber === 'number'
      ? submissionPageNumber === 0
      : totalPages === 1;
  const areTherePostConversionPages =
    typeof submissionPageNumber === 'number' &&
    submissionPageNumber < totalPages - 1;

  const {
    isDynamicHeightEnabled,
    maximumWidgetHeight,
    minimumWidgetHeight,
    isAutoScrollEnabled,
    fontOverride,
    iFrameId,
    widgetResizeDuration,
    isConsentStatementAboveNavigation:
      isConsentStatementAboveNavigationFormConfig,
    haveAllInitialMessagesArrived,
  } = useSelector((store) => store.optionalFormConfig);
  const { requiredWidgetHeight, isWidgetHeightRecalculationRequired } =
    useSelector((store) => store.dynamicHeight);
  const { submissionStatus, submissionBlocks } = useSelector(
    (store) => store.submission
  );
  const isConsentStatementAboveNavigation =
    isConsentStatementAboveNavigationFormConfig ||
    supplierMatchesRequireLegalStatementAboveNav;
  const dispatch = useDispatch();
  const [fontLoadedState, setFontLoadedState] = useState(false);
  const [minimumInputAreaHeight, setMinimumInputAreaHeight] = useState('0px');
  const [
    isDynamicHeightExceedingMaximumHeight,
    setIsDynamicHeightExceedingMaximumHeight,
  ] = useState(false);
  const [
    areDimensionChangeListenersConfigured,
    setAreDimensionChangeListenersConfigured,
  ] = useState(false);
  /**
   * The pageChangeTracker is an object with keys representing the page number and values representing the page change status.
   *
   * The page change status is an array with 3 elements: [enterPhase, enterPhase, enterPhase]
   *
   * The exitPhase and enterPhase are booleans that represent the current state of the page change.
   *
   * exitPhase: false -> true -> false (i.e. EXIT phase is complete), this is outside the scope of this component, but can be used to guard against premature calculations while the exit of components is in progress.
   *
   * enterPhase: false -> true -> false (i.e. ENTER phase is complete)
   */
  const [pageChangeTracker, updatePageChangeTracker] = useState({});

  /*
   * Track these state values via a ref which allows their up-to-date value to
   * be accessed by delayed setTimeout callbacks and other JS closures. If we
   * didn't do this then any callbacks of a setTimeout, for example, would
   * take the stale state value from before the pre-callback delay to use
   * within the callback rather than reference the latest/most up-to-date
   * state value. Simply put, if a setTimeout callback is set to trigger
   * 2 seconds after getting declared (and it happens to depend on state/store
   * values), then any updates to those state/store values within those 2
   * seconds will not get respected by the callback function body after the 2
   * seconds. This can lead to peculiar bugs like endless loops.
   */
  const isWidgetHeightRecalculationRequiredRef = useRef(false);
  const isDynamicHeightExceedingMaximumHeightRef = useRef(false);
  const submissionStatusRef = useRef(SubmissionStatus.NOT_STARTED);
  const isAwaitingFlagDrivenRefetchRef = useRef(false);
  if (isAwaitingFlagDrivenRefetchRef.current !== isAwaitingFlagDrivenRefetch) {
    isAwaitingFlagDrivenRefetchRef.current = isAwaitingFlagDrivenRefetch;
  }
  if (
    isWidgetHeightRecalculationRequiredRef.current !==
    isWidgetHeightRecalculationRequired
  ) {
    isWidgetHeightRecalculationRequiredRef.current =
      isWidgetHeightRecalculationRequired;
  }
  if (
    isDynamicHeightExceedingMaximumHeightRef.current !==
    isDynamicHeightExceedingMaximumHeight
  ) {
    isDynamicHeightExceedingMaximumHeightRef.current =
      isDynamicHeightExceedingMaximumHeight;
  }
  if (submissionStatusRef.current !== submissionStatus) {
    submissionStatusRef.current = submissionStatus;
  }

  const [formInputsSectionHeight, setFormInputsSectionHeight] = useState(0);
  const [pageNumbersOfStaticLoaders, setPageNumbersOfStaticLoaders] = useState(
    []
  );
  const [isCurrentPageStaticLoader, setIsCurrentPageStaticLoader] =
    useState(undefined);
  const [
    visibleComponentLevelErrorMessages,
    setVisibleComponentLevelErrorMessages,
  ] = useState([]);
  const [fixedHeightRemovalSchedule, setFixedHeightRemovalSchedule] = useState({
    onExited: false,
  });
  const [isInputAreaResizeInProgress, setIsInputAreaResizeInProgress] =
    useState(false);
  const [hasInputAreaBeenResized, setHasInputAreaBeenResized] = useState(false);

  /**
   * It takes the DOM ~300 - 330 ms to settle after changing the display
   * setting of the main form container from 'block' (which supports the
   * dynamically resizing inputAreaParentContainer) to 'flex' (supported
   * by the static height and ScrollContainer UX).
   */
  const gridDisplayStyleAdjustmentDelay = 350;
  const { isRendererAvailable, loaderConfig } = useSelector(
    (store) => store.lottieAnimationData
  );
  const haveAnimationsLoaded =
    loaderConfig &&
    Object.keys(loaderConfig).some((key) => loaderConfig[key].animationJson);
  const isLottieAnimationDataAndRendererReady =
    haveAnimationsLoaded && isRendererAvailable;
  const privacyPolicyLocale = getLocaleForPrivacyPolicy(
    phoneNumberCountryCode,
    countryCode,
    languageCode,
    localeCode
  );

  const previousPageRef = useRef();
  let previousPage;
  if (previousPageRef.current) {
    previousPage = previousPageRef.current;
    updateAndExposeDynamicHeightData({
      hasTheUserNavigatedAtLeastOnce: true,
    });
  }

  const hasPageJustChanged =
    !isPageChangeCompleted({ pageChangeTracker, currentPage }) &&
    currentPage !== previousPage &&
    (typeof previousPage === 'number' || currentPage === 1);

  const currentPageErrorMessages = Object.values(components)
    .filter(
      (component) =>
        component.component.page === currentPage &&
        component?.validation?.constraints &&
        getErrorMessage(component.validation.constraints)
    )
    .map((component) => getErrorMessage(component.validation.constraints));

  /**
   * Restore the unrestricted variable height properties of the inputArea parent div
   * This allows dynamic height to operate as usual and adjust the input area.
   * GOLD-552: Min height of the inputArea continues to be restricted.
   */
  const disableFixedHeightOfInputArea = () => {
    const inputAreaDomQueryString = isDynamicHeightExceedingMaximumHeight
      ? "[data-testid='inputAreaContainer']"
      : '#inputAreaParentContainer';
    const inputAreaParentContainer = document.querySelector(
      inputAreaDomQueryString
    );
    if (inputAreaParentContainer?.style) {
      inputAreaParentContainer.style.maxHeight = 'none';
    }
  };

  /**  initiateWidgetHeightFailsafeAlgorithm
   * Runs a recurring function within a setInterval() statement. The purpose of
   * this function is to poll or regularly examine the widget height requirements
   * through a dedicated helper function called reExamineRequiredWidgetHeight().
   *
   * Performance is respected by tying the frequency of re-running that helper
   * function proportionally to how recently a user interaction has occurred.
   * In simple terms: The more recently a user interaction has occurred, the
   * more frequently this algorithm will re-run the helper function
   * reExamineRequiredWidgetHeight(). At the very least, the helper will run
   * every 4 seconds, and at the most every 100 milliseconds.
   */
  const initiateWidgetHeightFailsafeAlgorithm = () => {
    const widgetHeightFailsafeAlgorithm = () => {
      //
      // Step 1 of 4 - Load latest constants for this algorithm iteration
      //

      const { failsafeAlgorithmData, shouldPostponeWidgetResize } =
        window?.__private__?.dynamicWidgetHeightData || {};
      if (!failsafeAlgorithmData) return;

      const {
        iterationSkipCounter,
        isRequiredWidgetHeightReExaminationPending,
        timeStampOfLastUserInteraction,
        previousTimeSinceLastUserInteraction,
        totalIterationsToSkip,
        minimumIterationsToSkip,
        maximumIterationsToSkip,
        timeStampOfLastWidgetHeightReExamination,
      } = failsafeAlgorithmData;
      const updatedFailsafeAlgorithmData = {};

      //
      // Step 2 of 4 - Define internal helper functions
      //

      /** assessNecessityForWidgetHeightReExamination
       * This function fulfills the main aim of this entire algorithm, it
       * decides whether to invoke 'reExamineRequiredWidgetHeight()' (i.e.
       * check that the widget height is where it should be, which is the most
       * expensive / DOM query heavy operation in this algorithm).
       * This check is what the entire algorithm polls for in varying frequency.
       * To make the decision to invoke or not to invoke that function depends
       * on:
       * - whether there is a page transition in progress (this would merely
       *   delay the invocation)
       * - whether an inputAreaParentContainer can be found in the DOM
       * - how long it's been since the last invocation (this would force the
       *   invocation if it exceeds a certain custom threshold, e.g. 4 seconds)
       */
      const assessNecessityForWidgetHeightReExamination = () => {
        const isPageTransitionInProgress = enterPhase || exitPhase;
        const inputAreaDomQueryString =
          isDynamicHeightExceedingMaximumHeightRef?.current
            ? "[data-testid='inputAreaContainer']"
            : '#inputAreaParentContainer';
        const inputAreaParentContainer = document.querySelector(
          inputAreaDomQueryString
        );
        const timeSinceLastWidgetHeightReExamination =
          timeStampOfLastWidgetHeightReExamination
            ? Date.now() - timeStampOfLastWidgetHeightReExamination
            : 0;
        const isLastWidgetHeightReExaminationMoreThanFourSecondsAgo =
          timeSinceLastWidgetHeightReExamination >= 4000;
        const initiateWidgetHeightReExamination = () => {
          if (shouldPostponeWidgetResize) {
            updateAndExposeDynamicHeightData({
              shouldPostponeWidgetResize: false,
            });
          }
          logToConsole.debug(
            '[DYNAMIC HEIGHT] Impending Widget Height Re-Examination'
          );
          reExamineRequiredWidgetHeight(
            dispatch,
            isWidgetHeightRecalculationRequiredRef.current
          );
        };

        const isWidgetHeightReExaminationDue =
          !!inputAreaParentContainer &&
          ((!isPageTransitionInProgress && !shouldPostponeWidgetResize) ||
            isLastWidgetHeightReExaminationMoreThanFourSecondsAgo);
        if (isWidgetHeightReExaminationDue) {
          if (isPageTransitionInProgress) {
            // The setTimeout below ensures that any ongoing page transition has completed since they take exactly as long as this value 'widgetResizeDuration' to complete
            setTimeout(() => {
              initiateWidgetHeightReExamination();
            }, widgetResizeDuration);
          } else {
            initiateWidgetHeightReExamination();
          }
          updatedFailsafeAlgorithmData.isRequiredWidgetHeightReExaminationPending = false;
        } else {
          updatedFailsafeAlgorithmData.isRequiredWidgetHeightReExaminationPending = true;
        }
      };

      /** updatePollingFrequencyControlVariables
       * Adjusts the polling control variables (variables that determine how
       * many iterations of the algorithm return early without actually assessing
       * the widget height). Depending on how recently the user has interacted
       * with the UI, the number of iterations to skip will get updated
       * accordingly:
       * - If there has never been a user interaction or it happened longer ago
       *   than the last time this helper function ran:
       *   ... then the number of iterations to skip rise (i.e.
       *   the polling frequency gets reduced)
       * - If, however, the last user interaction happened more recently
       *   than the last time this helper function ran:
       *   ... then the number of iterations to skip gets set back to its
       *   minimum (i.e. the polling frequency rises)
       */
      const updatePollingFrequencyControlVariables = () => {
        const reducePollingFrequencyOfWidgetHeightReExamination = () => {
          updatedFailsafeAlgorithmData.totalIterationsToSkip = Math.min(
            totalIterationsToSkip * 2,
            maximumIterationsToSkip
          );
          logToConsole.debug(
            '[DYNAMIC HEIGHT] Dynamic Height Failsafe Algorithm - ',
            'Reducing polling frequency to ...',
            `Once every ${
              updatedFailsafeAlgorithmData.totalIterationsToSkip * 20
            } [ms]`
          );
        };
        const haveThereBeenAnyUserInteractions =
          !!timeStampOfLastUserInteraction;
        if (haveThereBeenAnyUserInteractions) {
          const timeSinceLastUserInteraction =
            Date.now() - timeStampOfLastUserInteraction;
          const hasTheUserInteractedLessRecently = // Simply put: Are user interactions happening less often ?
            previousTimeSinceLastUserInteraction &&
            previousTimeSinceLastUserInteraction < timeSinceLastUserInteraction;
          updatedFailsafeAlgorithmData.previousTimeSinceLastUserInteraction =
            timeSinceLastUserInteraction;
          if (hasTheUserInteractedLessRecently) {
            // Reduce polling frequency = Increase the time it takes to re-examine the required widget height
            reducePollingFrequencyOfWidgetHeightReExamination();
          } else {
            // Maximise polling frequency = Reset the time it takes to re-examine the required widget height
            updatedFailsafeAlgorithmData.totalIterationsToSkip =
              minimumIterationsToSkip;
            logToConsole.debug(
              '[DYNAMIC HEIGHT] Dynamic Height Failsafe Algorithm - ',
              'Maximising polling frequency to ...',
              `Once every ${minimumIterationsToSkip * 20} [ms]`
            );
          }
        } else {
          // Reduce polling frequency = Increase the time it takes to re-examine the required widget height
          reducePollingFrequencyOfWidgetHeightReExamination();
        }
        updatedFailsafeAlgorithmData.iterationSkipCounter =
          updatedFailsafeAlgorithmData.totalIterationsToSkip;
      };

      //
      // Step 3 of 4 - Logical ENTRY POINT of ALGORITHM FLOW
      //

      if (isRequiredWidgetHeightReExaminationPending) {
        assessNecessityForWidgetHeightReExamination();
      } else if (iterationSkipCounter === 0) {
        updatePollingFrequencyControlVariables();
        assessNecessityForWidgetHeightReExamination();
      } else {
        updatedFailsafeAlgorithmData.iterationSkipCounter =
          iterationSkipCounter - 1;
      }

      //
      // Step 4 of 4 - Update algorithm variables by sending their new values through ahead of the next iteration
      //

      updateAndExposeDynamicHeightData({
        failsafeAlgorithmData: {
          ...failsafeAlgorithmData,
          ...updatedFailsafeAlgorithmData,
        },
      });
    };

    const intervalId = setInterval(widgetHeightFailsafeAlgorithm, 20);
    const { failsafeAlgorithmData } =
      window?.__private__?.dynamicWidgetHeightData || {};
    updateAndExposeDynamicHeightData({
      failsafeAlgorithmData: {
        ...failsafeAlgorithmData,
        intervalId,
      },
    });
  };

  /**  calculateAndUpdateMinimumInputAreaHeight
   * Calculates the new minimum height value for the inputAreaParentContainer
   * Returns the minimum height as a string value of this format: '123px'
   */
  const calculateAndUpdateMinimumInputAreaHeight = (
    shouldChainWidgetRecalculation = false
  ) => {
    /**
     * NOTE: Whenever this function gets called from within an event listener it cannot
     *       access the latest values from local or store state. It must therefore
     *       load all of its dependencies within the function body. Even input arguments
     *       are defined statically at the point of defining the event listener.
     *       Another possible solution to this would be to use React refs as a more
     *       up-to-date reference which has been done at the top of this component.
     */
    const { headerRef, footerRef, isLegalStatementMountingOrUnmounting } =
      window?.__private__?.dynamicWidgetHeightData || {};
    if (isLegalStatementMountingOrUnmounting) {
      return undefined;
    }
    const outerFooterContainer = footerRef?.current;
    const footerSectionHeight =
      outerFooterContainer?.getBoundingClientRect().height;
    const headerSectionHeight =
      headerRef?.current?.getBoundingClientRect().height;
    const legalStatementContainer = document.querySelector(
      "[data-testid='legalStatementContainer']"
    );
    // The reason for this legalStatementHeightBuffer is GOLD-552 (https://mvfglobal.atlassian.net/browse/GOLD-552)
    const legalStatementHeightBuffer = legalStatementContainer
      ? (legalStatementContainer?.getBoundingClientRect()?.height || 0) +
        parseInt(
          window.getComputedStyle(legalStatementContainer)?.marginBottom || 0,
          10
        )
      : 0;
    const minInputAreaHeight =
      legalStatementHeightBuffer +
      minimumWidgetHeight -
      (headerSectionHeight + footerSectionHeight);
    const maxInputAreaHeight =
      maximumWidgetHeight - (headerSectionHeight + footerSectionHeight);

    if (
      typeof minInputAreaHeight === 'number' &&
      !Number.isNaN(minInputAreaHeight) &&
      typeof maxInputAreaHeight === 'number' &&
      !Number.isNaN(maxInputAreaHeight)
    ) {
      setMinimumInputAreaHeight(`${Math.floor(minInputAreaHeight)}px`);
      if (shouldChainWidgetRecalculation) {
        if (isWidgetHeightRecalculationRequired) {
          dispatch(
            updateRecalculateRequiredWidgetHeight({
              isWidgetHeightRecalculationRequired: false,
            })
          );
        }
        dispatch(
          updateRecalculateRequiredWidgetHeight({
            isWidgetHeightRecalculationRequired: true,
          })
        );
      }
      return minInputAreaHeight;
    }
    setMinimumInputAreaHeight('0px');
    return 0;
  };

  const setUpDimensionChangeListenersForAppContainers = (
    inputAreaOnly = false
  ) => {
    const { headerRef, footerRef, resizeObservers } =
      window?.__private__?.dynamicWidgetHeightData || {};
    const outerHeaderContainer = headerRef?.current;
    const inputAreaDomQueryString = isDynamicHeightExceedingMaximumHeight
      ? "[data-testid='inputAreaContainer']"
      : '#inputAreaParentContainer';
    const inputAreaParentContainer = document.querySelector(
      inputAreaDomQueryString
    );
    if (!inputAreaParentContainer) return;

    const outerFooterContainer = footerRef?.current;

    const inputAreaAndFullWidgetHeightRecalculationCallback = () => {
      calculateAndUpdateMinimumInputAreaHeight(true);
    };

    if (window.ResizeObserver) {
      /**
       * Set up a dimensions listener on the 3 form section elements to allow
       * height recalculations of the inputArea and the full widget itself to
       * keep up with both the changing height requirements of the whole widget
       * and the shifts in height ratios within the widget whenever the user
       * externally manipulates the dimensions of the widget.
       */
      /**
       * Step 1 of 3 - Cleanup any existing memory that is occupied by
       * previously added resize observers (done by unobserving the
       * DOM elements in question after retrieving the resize observers from
       * the memory reference).
       */
      if (resizeObservers && Array.isArray(resizeObservers)) {
        resizeObservers.forEach((resizeObserverMap) => {
          resizeObserverMap.observer.unobserve(
            resizeObserverMap.observedDomElement
          );
        });
      }
      // Step 2 of 3 - Observe the DOM elements
      const inputAreaObserver = new ResizeObserver(
        inputAreaAndFullWidgetHeightRecalculationCallback
      );
      const headerContainerObserver = new ResizeObserver(
        inputAreaAndFullWidgetHeightRecalculationCallback
      );
      const footerContainerObserver = new ResizeObserver(
        inputAreaAndFullWidgetHeightRecalculationCallback
      );
      const observers = [
        {
          observer: inputAreaObserver,
          observedDomElement: inputAreaParentContainer,
        },
      ];
      inputAreaObserver.observe(inputAreaParentContainer); // listens efficiently to dimension changes
      if (!inputAreaOnly) {
        headerContainerObserver.observe(outerHeaderContainer);
        footerContainerObserver.observe(outerFooterContainer);
        observers.push({
          observer: headerContainerObserver,
          observedDomElement: outerHeaderContainer,
        });
        observers.push({
          observer: footerContainerObserver,
          observedDomElement: outerFooterContainer,
        });
      }
      // Step 3 of 3 - Keep a memory reference of all resize observers
      updateAndExposeDynamicHeightData({ resizeObservers: observers });
    } else {
      /**
       * This workaround (for unsupported browsers) is not exactly a polyfill
       * but it successfully detects widget width changes whenever they are
       * caused by mobile screen orientation changes or by window resizing
       * Expect this code to kick in for browsers excluded here:
       * https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver#browser_compatibility
       */

      /**
       * Note: All event listeners that this function sets up against the
       *       inputAreaParentContainer may safely be attached without
       *       worrying about explicitly removing them.
       *       The reasons behind this are two-fold:
       *       1 This function (setUpDimensionChangeListenersForAppContainers)
       *         only gets invoked in 2 scenarios (on initial mount of
       *         <DynamicWidgetHeight/> & after the unmount and re-mount of
       *         inputAreaParentContainer as part of the emergence or removal of
       *         a ScrollContainer whenever dynamic height exceeds/or falls below
       *         the maximumWidgetHeight threshold). Unmounting a DOM element
       *         cleanses all of its event listeners from memory.
       *       2 The event listeners we set up in this function need to
       *         remain in place for the entire lifetime of the
       *         inputAreaParentContainer.
       *       However, event listeners tied to the header and footer elements do
       *       need to be removed explicitly whenever this function gets called.
       *       This is because those elements do not get unmounted as part of the
       *       scenario laid out in point 1 above.
       */
      inputAreaParentContainer.addEventListener(
        'orientationchange',
        inputAreaAndFullWidgetHeightRecalculationCallback
      );
      inputAreaParentContainer.addEventListener(
        'resize',
        inputAreaAndFullWidgetHeightRecalculationCallback
      );
      if (!inputAreaOnly) {
        // Step 1 of 2 - Remove previously added listeners from the header and footer elements
        outerHeaderContainer.removeEventListener(
          'orientationchange',
          inputAreaAndFullWidgetHeightRecalculationCallback
        );
        outerHeaderContainer.removeEventListener(
          'resize',
          inputAreaAndFullWidgetHeightRecalculationCallback
        );
        outerFooterContainer.removeEventListener(
          'orientationchange',
          inputAreaAndFullWidgetHeightRecalculationCallback
        );
        outerFooterContainer.removeEventListener(
          'resize',
          inputAreaAndFullWidgetHeightRecalculationCallback
        );
        // Step 2 of 2 - Add new listeners to the header and footer elements
        outerHeaderContainer.addEventListener(
          'orientationchange',
          inputAreaAndFullWidgetHeightRecalculationCallback
        );
        outerHeaderContainer.addEventListener(
          'resize',
          inputAreaAndFullWidgetHeightRecalculationCallback
        );
        outerFooterContainer.addEventListener(
          'orientationchange',
          inputAreaAndFullWidgetHeightRecalculationCallback
        );
        outerFooterContainer.addEventListener(
          'resize',
          inputAreaAndFullWidgetHeightRecalculationCallback
        );
      }
    }
    setAreDimensionChangeListenersConfigured(true);
  };

  const prepareInputAreaForDynamicHeightAdjustments = (
    inputAreaParentContainer
  ) => {
    if (
      !inputAreaParentContainer?.style ||
      !inputAreaParentContainer?.parentNode
    ) {
      return;
    }
    // Styling update - Part 1 of 2
    updateAndExposeDynamicHeightData({
      hasDynamicHeightDomInitialisationStarted: true,
    });

    /**
     * Failsafe logic - hasDynamicHeightDomInitialisationStarted
     * - ensures that dynamic height related dom styling steps have completed
     *   successfully within 2 seconds or get reattempted in case they were
     *   interrupted by an unforeseen event (e.g. a user action), unless the UI
     *   has transitioned to a static height flow meanwhile due to exceeding the
     *   maximumWidgetHeight threshold.
     */
    setTimeout(() => {
      const dynamicInputAreaContainer = document.querySelector(
        '#inputAreaParentContainer'
      );
      const { hasDynamicHeightDomInitialisationStarted } =
        window?.__private__?.dynamicWidgetHeightData || {};
      if (
        hasDynamicHeightDomInitialisationStarted === true &&
        dynamicInputAreaContainer
      ) {
        // Re-try
        prepareInputAreaForDynamicHeightAdjustments(dynamicInputAreaContainer);
      }
    }, 2000);
    const { hasTheUserNavigatedAtLeastOnce } =
      window?.__private__?.dynamicWidgetHeightData || {};
    const minInputAreaHeight = calculateAndUpdateMinimumInputAreaHeight() || 0;
    const currentInputAreaHeight = hasTheUserNavigatedAtLeastOnce
      ? Math.ceil(determineRequiredHeightOfFormInputsSection())
      : inputAreaParentContainer?.getBoundingClientRect()?.height;
    logToConsole.debug(
      '[DYNAMIC HEIGHT] hasTheUserNavigatedAtLeastOnce',
      hasTheUserNavigatedAtLeastOnce
    );
    logToConsole.debug(
      '[DYNAMIC HEIGHT] currentInputAreaHeight',
      currentInputAreaHeight
    );
    // Guarded because building these log arguments forces layout reflow
    if (logToConsole.isDebug()) {
      logToConsole.debug(
        '[DYNAMIC HEIGHT] UPDATED formInputsSectionHeight',
        determineRequiredHeightOfFormInputsSection()
      );
      logToConsole.debug(
        '[DYNAMIC HEIGHT] inputAreaParentContainer?.getBoundingClientRect()?.height',
        inputAreaParentContainer?.getBoundingClientRect()?.height
      );
    }
    const newInputAreaHeight = currentInputAreaHeight
      ? `${Math.floor(
          Math.max(minInputAreaHeight, parseFloat(currentInputAreaHeight))
        )}px`
      : minInputAreaHeight;
    updateAndExposeDynamicHeightData({
      requiredInputAreaHeight: undefined,
      shouldResizeInputArea: false,
    });
    // eslint-disable-next-line no-param-reassign
    inputAreaParentContainer.style.minHeight = newInputAreaHeight;
    // eslint-disable-next-line no-param-reassign
    inputAreaParentContainer.style.maxHeight = 'none';
    logToConsole.debug(
      '[DYNAMIC HEIGHT] Setting inputAreaParentContainer.style.minHeight to: ',
      newInputAreaHeight
    );

    setTimeout(
      () => {
        /** Styling update - Part 2 of 2
         * Since there is a significant delay between the first part of this styling
         * update and the second one, and the user may have triggered an action that
         * causes the UI to change away from the dynamic height DOM structure
         * to the static height DOM structure (e.g. by causing a form error that
         * pushed the UI height over the maximumWidgetHeight threshold ...), this
         * guard clause is in place to make sure that the style changes from part 1
         * are still in place.
         */
        // eslint-disable-next-line no-shadow
        logToConsole.debug(
          '[DYNAMIC HEIGHT] inputAreaParentContainer.style.minHeight === newInputAreaHeight',
          inputAreaParentContainer.style.minHeight === newInputAreaHeight,
          'inputAreaParentContainer?.parentNode?.style',
          inputAreaParentContainer?.parentNode?.style
        );
        if (
          inputAreaParentContainer.style.minHeight === newInputAreaHeight &&
          inputAreaParentContainer?.parentNode?.style
        ) {
          // eslint-disable-next-line no-param-reassign
          inputAreaParentContainer.parentNode.style.display = 'block';
          updateAndExposeDynamicHeightData({
            hasDynamicHeightDomInitialisationStarted: false,
          });
        }
      },
      hasTheUserNavigatedAtLeastOnce ? gridDisplayStyleAdjustmentDelay : 0
    );
  };

  useEffect(() => {
    if (!isDynamicHeightEnabled) {
      return;
    }
    if (
      isSinglePageForm &&
      isLegalStatementRevealed &&
      isConsentStatementAboveNavigation
    ) {
      updateAndExposeDynamicHeightData({
        isLegalStatementMountingOrUnmounting: false,
      });
      setTimeout(() => {
        dispatch(
          updateRecalculateRequiredWidgetHeight({
            isWidgetHeightRecalculationRequired: true,
          })
        );
      }, 0);
    }
  }, [isLegalStatementRevealed]);

  useEffect(() => {
    if (
      haveAllInitialMessagesArrived &&
      isDynamicHeightEnabled &&
      minimumInputAreaHeight === '0px'
    ) {
      setUpDimensionChangeListenersForAppContainers();
      const inputAreaDomQueryString = isDynamicHeightExceedingMaximumHeight
        ? "[data-testid='inputAreaContainer']"
        : '#inputAreaParentContainer';
      const inputAreaParentContainer = document.querySelector(
        inputAreaDomQueryString
      );
      if (!inputAreaParentContainer || isDynamicHeightExceedingMaximumHeight) {
        return;
      }
      prepareInputAreaForDynamicHeightAdjustments(inputAreaParentContainer);
      const { failsafeAlgorithmData } =
        window?.__private__?.dynamicWidgetHeightData || {};
      if (!failsafeAlgorithmData) {
        updateAndExposeDynamicHeightData({
          failsafeAlgorithmData: baseFailsafeAlgorithmHeightData,
        });
      } else {
        updateAndExposeDynamicHeightData({
          failsafeAlgorithmData: {
            ...baseFailsafeAlgorithmHeightData,
            ...failsafeAlgorithmData,
          },
        });
      }
      initiateWidgetHeightFailsafeAlgorithm();
    }
  }, [haveAllInitialMessagesArrived]);

  /**
   * Whenever the widget height has just started to exceed the maximumWidgetHeight
   * value, the inputAreaParentContainer gets unmounted and replaced by the
   * ScrollContainer element. This means that when the widget height has just
   * fallen below the maximumWidgetHeight (i.e. isDynamicHeightExceedingMaximumHeight = false)
   * the dimension change listeners need to be readded to the element.
   */
  useEffect(() => {
    logToConsole.debug(
      '[DYNAMIC HEIGHT] isDynamicHeightExceedingMaximumHeight has changed to: ',
      isDynamicHeightExceedingMaximumHeight
    );
    const inputAreaDomQueryString =
      isDynamicHeightExceedingMaximumHeightRef.current
        ? "[data-testid='inputAreaContainer']"
        : '#inputAreaParentContainer';
    const inputAreaParentContainer = document.querySelector(
      inputAreaDomQueryString
    );
    if (
      !isDynamicHeightExceedingMaximumHeightRef.current &&
      // The second boolean guards against running this block after first mount
      haveAllInitialMessagesArrived
    ) {
      if (inputAreaParentContainer) {
        prepareInputAreaForDynamicHeightAdjustments(inputAreaParentContainer);
      }

      if (areDimensionChangeListenersConfigured) {
        // Only reconfigure the listener for the now newly mounted inputAreaParentContainer
        setUpDimensionChangeListenersForAppContainers(true);
      } else {
        // Configure the listeners for all three form sub-containers)
        setUpDimensionChangeListenersForAppContainers();
      }
    }
    if (
      // The requiredWidgetHeight has just fallen below the maximumWidgetHeight
      !isDynamicHeightExceedingMaximumHeight &&
      // The second boolean guards against running this block after first mount
      haveAllInitialMessagesArrived
    ) {
      if (inputAreaParentContainer) {
        logToConsole.debug(
          '[DYNAMIC HEIGHT] isDynamicHeightExceedingMaximumHeight = false',
          'calling prepareInputAreaForDynamicHeightAdjustments()'
        );
        prepareInputAreaForDynamicHeightAdjustments(inputAreaParentContainer);
      }
    }
  }, [isDynamicHeightExceedingMaximumHeight]);

  useEffect(() => {
    const isDynamicHeightWithinMaximumHeight =
      requiredWidgetHeight <= maximumWidgetHeight;
    if (
      isDynamicHeightExceedingMaximumHeight !==
      !isDynamicHeightWithinMaximumHeight
    ) {
      setIsDynamicHeightExceedingMaximumHeight(
        !isDynamicHeightWithinMaximumHeight
      );
    }
  }, [requiredWidgetHeight]);

  useEffect(() => {
    if (!isDynamicHeightEnabled) return;
    if (exitPhase === false) {
      // EXIT PHASE has just finished. i.e. onExited
      if (fixedHeightRemovalSchedule.onExited) {
        disableFixedHeightOfInputArea();
        setFixedHeightRemovalSchedule({ onExited: false });
      }

      if (hasStatementGraduallyDisappeared) {
        dispatch(
          updateHasStatementGraduallyDisappeared({
            hasStatementGraduallyDisappeared: false,
          })
        );
      }
    }
  }, [exitPhase]);

  useEffect(() => {
    if (!isDynamicHeightEnabled) return;
    if (
      !isEqual(currentPageErrorMessages, visibleComponentLevelErrorMessages)
    ) {
      setVisibleComponentLevelErrorMessages(currentPageErrorMessages);
      updateAndExposeDynamicHeightData({
        shouldResizeInputArea: true,
        requiredInputAreaHeight: undefined,
      });
      if (isWidgetHeightRecalculationRequired) {
        dispatch(
          updateRecalculateRequiredWidgetHeight({
            isWidgetHeightRecalculationRequired: false,
          })
        );
      }
      dispatch(
        updateRecalculateRequiredWidgetHeight({
          isWidgetHeightRecalculationRequired: true,
        })
      );
    }
  }, [currentPageErrorMessages, visibleComponentLevelErrorMessages]);

  useEffect(() => {
    /* runs once - ensures all fonts are loaded on page 1 (to avoid relying on a
     * changing height requirement due to font resizes that change the space
     * requirements of page 1)
     */
    if (!document?.fonts) {
      document.fonts = {
        onloadingdone: () => {
          setFontLoadedState(true);
        },
      };
    } else {
      document.fonts.onloadingdone = () => {
        setFontLoadedState(true);
      };
    }
  }, []);

  /**
   * The following side effect manager will trigger a full widget height recalculation if formInputsSectionHeight changes.
   */
  useEffect(() => {
    if (!isDynamicHeightEnabled) return;
    const { inputAreaRef } = window?.__private__?.dynamicWidgetHeightData || {};
    if (
      inputAreaRef &&
      inputAreaRef?.current &&
      formInputsSectionHeight !== determineRequiredHeightOfFormInputsSection()
    ) {
      setFormInputsSectionHeight(determineRequiredHeightOfFormInputsSection());
    }
  });

  useEffect(() => {
    if (!isDynamicHeightEnabled) return;
    if (showAppointmentBooking === false) {
      /**
       * When wasPreviousPageAppointmentBooking changes, and we no longer are showing appointment booking, it means the previous page has transitioned back in. As the height of the input area has changed now, recalculate the height.
       */
      setFormInputsSectionHeight(determineRequiredHeightOfFormInputsSection());
    }
  }, [showAppointmentBooking, wasPreviousPageAppointmentBooking]);

  useEffect(() => {
    if (!isDynamicHeightEnabled) return;
    if (showCpcRecommendations === false) {
      /**
       * When wasPreviousPageCpcCard changes, and we no longer are showing cpc recommendations, it means the previous
       * page has transitioned back in. As the height of the input area has changed now, recalculate the height.
       */
      setFormInputsSectionHeight(determineRequiredHeightOfFormInputsSection());
    }
  }, [showCpcRecommendations, wasPreviousPageCpcCard]);

  useEffect(() => {
    /* This handler achieves two things:
     * - Instructs a recalculation of widget height after navigating from the
     *   interstitial loader page to the next page (fix for special case,
     *   https://mvfglobal.atlassian.net/browse/WF-4192)
     * - Tracks the previous page number
     *   Runs whenever the page has completed a nav transition (i.e. when the
     *   <Transition /> ENTER phase completes by toggling enterPhase to false).
     */
    if (!isDynamicHeightEnabled) return;
    if (enterPhase === false) {
      if (pageNumbersOfStaticLoaders.includes(previousPage)) {
        dispatch(
          updateRecalculateRequiredWidgetHeight({
            isWidgetHeightRecalculationRequired: true,
          })
        );
      }

      // On form pages that contain a legal statement, this call needs to happen at the end of the enter phase.
      disableFixedHeightOfInputArea();

      if (!isSinglePageForm) {
        updateAndExposeDynamicHeightData({
          blockNavigation: true,
          timeStampOfBlockNavigationUpdate: Date.now(),
        });
        logToConsole.debug('[DYNAMIC HEIGHT] NAV BLOCKED 2');
        const inputAreaDomQueryString = isDynamicHeightExceedingMaximumHeight
          ? "[data-testid='inputAreaContainer']"
          : '#inputAreaParentContainer';
        const inputAreaParentContainer = document.querySelector(
          inputAreaDomQueryString
        );

        if (!inputAreaParentContainer) {
          updateAndExposeDynamicHeightData({
            blockNavigation: false,
            timeStampOfBlockNavigationUpdate: Date.now(),
          });
          logToConsole.debug('[DYNAMIC HEIGHT] NAV UNBLOCKED 3');
          return;
        }

        const { shouldPostponeWidgetResize } =
          window?.__private__?.dynamicWidgetHeightData || {};
        /**
         * The block below reattempts required height calculations after the
         * previous attempts to calculate height revealed that the maximumWidgetHeight
         * threshold was about to be crossed during the ENTER phase of a page
         * transition. The widget height resize was then aborted and postponed.
         * This will now adjust the widget height to the required figure.
         */
        if (shouldPostponeWidgetResize) {
          updateAndExposeDynamicHeightData({
            shouldPostponeWidgetResize: false,
            isComplexWidgetResizeInProgress: true,
          });

          /**
           * Failsafe logic - isComplexWidgetResizeInProgress
           * Should this lengthier widget resize operation get interrupted mid-way,
           * by an unforeseen event such as a user action (e.g. a sudden nav-back attempt
           * that may mislead the height calculation process), this code will re-attempt
           * to correctly calculate the required widget height.
           * The separate useEffect which watches isWidgetHeightRecalculationRequired
           * will make sure that isComplexWidgetResizeInProgress doesn't stay true
           * permanently, which would prevent navigation.
           */
          setTimeout(() => {
            const { isComplexWidgetResizeInProgress } =
              window?.__private__?.dynamicWidgetHeightData || {};
            if (isComplexWidgetResizeInProgress && !exitPhase && !enterPhase) {
              dispatch(
                updateRecalculateRequiredWidgetHeight({
                  isWidgetHeightRecalculationRequired: true,
                })
              );
            }
          }, 1500);

          if (isWidgetHeightRecalculationRequired) {
            dispatch(
              updateRecalculateRequiredWidgetHeight({
                isWidgetHeightRecalculationRequired: false,
              })
            );
          }
          dispatch(
            updateRecalculateRequiredWidgetHeight({
              isWidgetHeightRecalculationRequired: true,
            })
          );
        } else {
          const { isComplexWidgetResizeInProgress } =
            window?.__private__?.dynamicWidgetHeightData || {};
          if (
            isComplexWidgetResizeInProgress &&
            !exitPhase &&
            !isWidgetHeightRecalculationRequired
          ) {
            setTimeout(() => {
              updateAndExposeDynamicHeightData({
                isComplexWidgetResizeInProgress: false,
              });
            }, 0);
          }

          setTimeout(() => {
            reExamineRequiredWidgetHeight(
              dispatch,
              isWidgetHeightRecalculationRequiredRef.current
            );
          }, 1500);
        }
        setTimeout(() => {
          updateAndExposeDynamicHeightData({
            blockNavigation: false,
            timeStampOfBlockNavigationUpdate: Date.now(),
          });
          // eslint-disable-next-line no-shadow
          logToConsole.debug('[DYNAMIC HEIGHT] NAV UNBLOCKED 5');
        }, 0);
      }

      // This operation must happen last in this handler as the prior value of previousPage is relied upon by the operation above
      previousPageRef.current = currentPage;
    }
  }, [enterPhase]);

  useEffect(() => {
    /* Runs whenever the page or enterPhase changes and tracks transition enterPhase stages of a navigation transition by mapping them against the current page number
     * A complete page change including navigation phases consists of the following 3 steps:
     * - The currentPage state changes from one number (x) to another (y) while the transition enterPhase is false (not yet reached) i.e. <Transition/> is in EXIT phase
     * - The enterPhase toggles from false to true i.e. <Transition/> begins ENTER phase
     * - The enterPhase toggles back to false i.e. <Transition/> completes ENTER phase
     */
    if (!isDynamicHeightEnabled) return;

    if (
      // currentPage has changed
      !pageChangeTracker[currentPage] ||
      !pageChangeTracker[currentPage].length
    ) {
      const currentPageChangeState = {};
      currentPageChangeState[currentPage] = [enterPhase];
      updatePageChangeTracker(currentPageChangeState);
    } else {
      // enterPhase has changed
      const currentPageChangeState = {};
      currentPageChangeState[currentPage] = [
        ...pageChangeTracker[currentPage],
        enterPhase,
      ];
      updatePageChangeTracker(currentPageChangeState);
    }
  }, [currentPage, enterPhase]);

  useEffect(() => {
    // this handler fixes a bug WF-3924 to do with the LegalStatement
    if (isDynamicHeightEnabled && shouldRenderLegalStatement) {
      dispatch(
        updateRecalculateRequiredWidgetHeight({
          isWidgetHeightRecalculationRequired: true,
        })
      );
    }
  }, [shouldRenderLegalStatement, privacyPolicyLocale, statement]);

  useEffect(() => {
    /* Responsible for establishing a nice UX when mounting and unmounting the legal consent statement.
     * Specifically, during first mount:
     * - the inputArea's height needs to remain fixed when rendering the <LegalStatement />
     * - its parent element (the widget's main container) also needs to be displayed as a
     *   block rather than a flex container. The same applies to the unmount journey.
     */
    if (!isDynamicHeightEnabled) return;

    const { inputAreaRef, shouldResizeInputArea } =
      window?.__private__?.dynamicWidgetHeightData || {};
    const currentInputAreaHeight =
      inputAreaRef?.current?.getBoundingClientRect().height || 0;
    const inputAreaDomQueryString = isDynamicHeightExceedingMaximumHeight
      ? "[data-testid='inputAreaContainer']"
      : '#inputAreaParentContainer';
    const inputAreaParentContainer = document.querySelector(
      inputAreaDomQueryString
    );

    if (handleSideEffectsOfLegalStatement === true) {
      updateAndExposeDynamicHeightData({
        isLegalStatementMountingOrUnmounting: true,
      });
      exposeEventToPartner({
        eventType: 'isAutoScrollTempDisabled',
        eventObject: {
          iFrameId,
          skipAutoScroll: true,
        },
      });

      if (currentInputAreaHeight && inputAreaParentContainer) {
        /*
         * A better user experience is attained when the legal consent statement that
         * appears prior to navigating to the penultimate page of the form doesn't push
         * up and squeeze the inputArea. To achieve that, the height of the inputAreaParentContainer
         * gets temporarily locked into place by setting its min and max height to
         * its current height just before the consent statement mounts.
         */
        const isThereALegalStatement = statement !== undefined;
        let newInputAreaHeight = currentInputAreaHeight;

        if (isThereALegalStatement && !shouldResizeInputArea) {
          if (newInputAreaHeight < parseInt(minimumInputAreaHeight, 10)) {
            newInputAreaHeight = parseInt(minimumInputAreaHeight, 10);
          }
          updateAndExposeDynamicHeightData({
            requiredInputAreaHeight: undefined,
            shouldResizeInputArea: false,
          });

          if (isCurrentPageStaticLoader) {
            const loaderComponentContainer = document.querySelector(
              "[data-testid='loaderContainer']"
            );
            if (loaderComponentContainer) {
              updateAndExposeDynamicHeightData({
                requiredInputAreaHeight: undefined,
                shouldResizeInputArea: false,
              });
              inputAreaParentContainer.style.minHeight = `${
                loaderComponentContainer.getBoundingClientRect().height
              }px`;
              inputAreaParentContainer.style.maxHeight = `${
                loaderComponentContainer.getBoundingClientRect().height
              }px`;
            }
          } else {
            updateAndExposeDynamicHeightData({
              requiredInputAreaHeight: undefined,
              shouldResizeInputArea: false,
            });
            inputAreaParentContainer.style.maxHeight = `${newInputAreaHeight}px`;
            inputAreaParentContainer.style.minHeight = `${newInputAreaHeight}px`;
          }
        }
        if (!isSinglePageForm) {
          updateAndExposeDynamicHeightData({
            blockNavigation: true,
            timeStampOfBlockNavigationUpdate: Date.now(),
          });
          logToConsole.debug('[DYNAMIC HEIGHT] NAV BLOCKED 3');
          /* This temporary display style change from 'flex' to 'block' on the widget's main
           * container makes sure that the newly appearing legal statement overflows below the
           * widget's viewport and doesn't cause the entire footer to get shifted off-screen to
           * the right of the widget's viewport. The subsequent dynamic resize will smoothly
           * reveal the legal statement.
           */
          if (isCurrentPageStaticLoader) {
            do {
              // Provides most of the relevant benefits of block display styles without incurring a 25px height shift
              inputAreaParentContainer.parentNode.style.flexWrap = 'wrap';
            } while (
              window.getComputedStyle(inputAreaParentContainer.parentNode)
                ?.flexWrap !== 'wrap'
            );
          } else {
            do {
              inputAreaParentContainer.parentNode.style.display = 'block';
            } while (
              window.getComputedStyle(inputAreaParentContainer.parentNode)
                ?.display !== 'block'
            );
          }

          if (!isThereALegalStatementOnTheCurrentPage) {
            // We are about to navigate to a page requiring the legal statement to be shown
            do {
              inputAreaParentContainer.style.display = 'block';
            } while (
              window.getComputedStyle(inputAreaParentContainer)?.display !==
              'block'
            );
          }
        }
        updateAndExposeDynamicHeightData({
          blockNavigation: false,
          timeStampOfBlockNavigationUpdate: Date.now(),
        });
        logToConsole.debug('[DYNAMIC HEIGHT] NAV UNBLOCKED 1');
        const legalStatementContainer = document.querySelector(
          '#legalStatementContainer'
        );
        const updatedValueForIsLegalStatementRevealed = isSinglePageForm
          ? isThereALegalStatementOnTheCurrentPage
          : !isThereALegalStatementOnTheCurrentPage;
        const isSubmissionDone =
          submissionStatusRef?.current === SubmissionStatus.COMPLETE;
        if (
          !isSinglePageForm ||
          areTherePostConversionPages ||
          (legalStatementContainer &&
            isLegalStatementRevealed !==
              updatedValueForIsLegalStatementRevealed)
        ) {
          setTimeout(() => {
            dispatch(
              updateIsLegalStatementRevealed({
                isLegalStatementRevealed:
                  isSinglePageForm && !isSubmissionDone
                    ? isThereALegalStatementOnTheCurrentPage
                    : !isThereALegalStatementOnTheCurrentPage,
              })
            );
            setFixedHeightRemovalSchedule({ onExited: true });
            dispatch(
              setHandleSideEffectsOfLegalStatement({
                handleSideEffectsOfLegalStatement: false,
              })
            );

            if (!isSinglePageForm || areTherePostConversionPages) {
              if (
                sideEffectDetailsForLegalStatement?.navigationDirection ===
                'backwards'
              ) {
                dispatch(
                  setShouldNavigateBackward({
                    shouldNavigateBackward: true,
                  })
                );
              }
              if (
                sideEffectDetailsForLegalStatement?.navigationDirection ===
                'forwards'
              ) {
                dispatch(
                  setShouldNavigateForward({
                    shouldNavigateForward: true,
                  })
                );
              }
            }
          }, widgetResizeDuration);
        } else {
          dispatch(
            setHandleSideEffectsOfLegalStatement({
              handleSideEffectsOfLegalStatement: false,
            })
          );
          dispatch(
            setSideEffectDetailsForLegalStatement({
              sideEffectDetailsForLegalStatement: {},
            })
          );
        }
        dispatch(
          updateShouldRenderLegalStatement({
            shouldRenderLegalStatement:
              isSinglePageForm && !isSubmissionDone
                ? isThereALegalStatementOnTheCurrentPage
                : !isThereALegalStatementOnTheCurrentPage,
          })
        );
      }
    } else {
      updateAndExposeDynamicHeightData({
        isLegalStatementMountingOrUnmounting: false,
      });
    }
  }, [currentPage, handleSideEffectsOfLegalStatement]);

  useEffect(() => {
    if (
      isDynamicHeightEnabled &&
      currentPage !== undefined &&
      maximumWidgetHeight &&
      minimumWidgetHeight &&
      !window.__private__?.isEnterAnimationInProgress // The <Transition/> component may cause additional runs of this component and its effect handlers. To prevent excessive runs of this logic, ongoing enter animations / page transitions will prevent/skip this entire block
    ) {
      dispatch(
        updateRecalculateRequiredWidgetHeight({
          isWidgetHeightRecalculationRequired: true,
        })
      );
    }
  }, [
    currentPage,
    enterPhase, // The page transition manager <Transition/> sets this store value to true the moment the old page is fully unmounted and the new page has begun to mount
    maximumWidgetHeight,
    minimumWidgetHeight,
    formInputsSectionHeight,
  ]);

  useEffect(() => {
    /* Responsible for establishing a nice UX on back navigation from the final
     * form page to a previous page after unmounting the legal consent statement.
     *
     * kicks in only if isConsentStatementAboveNavigation = false
     *
     * The problem at hand:
     * Specifically, some forms have a small final form page
     * i.e. it is right at the minimumWidgetHeight whilst the consent statement
     * is still visible. Once a user chooses to navigate back, the removal of the
     * consent statement means that the remaining page content is below the
     * minimumWidgetHeight mark. In such scenarios, the footer section is briefly
     * floating above the bottom of the widget.
     * The issue with that is that once the back navigation transition takes place
     * the ENTER phase introduces a typically larger input area (with more
     * question elements present) which end up pushing the footer down in a
     * harsh, jumpy fashion (constituting a bad UX).
     *
     * The solution:
     * What this handler does to solve this issue is that it introduces a smoothing
     * transition property that shifts the footer gently down towards the bottom
     * edge of the widget area, just before the back navigation transition kicks
     * off. This CSS transition is temporary and will get taken off by a later
     * useEffect handler (triggering when enterPhase has just become false, aka
     * onEXITED).
     *
     * NOTE: If isConsentStatementAboveNavigation = true, different logic inside
     *       this file src/Components/NavigationBar/navigationBar.helpers.js
     *       ensures an acceptable UX. To find that logic, look for the boolean
     *       widgetHeightIsAtItsMinimum.
     */

    if (!isDynamicHeightEnabled) return;
    if (shouldRenderLegalStatement === false) {
      setTimeout(() => {
        const inputAreaDomQueryString =
          isDynamicHeightExceedingMaximumHeightRef.current
            ? "[data-testid='inputAreaContainer']"
            : '#inputAreaParentContainer';
        const inputAreaParentContainer = document.querySelector(
          inputAreaDomQueryString
        );
        const inputAreaHeight =
          inputAreaParentContainer?.getBoundingClientRect().height;
        const { headerRef, footerRef } =
          window?.__private__?.dynamicWidgetHeightData || {};
        const headerSectionHeight =
          headerRef?.current?.getBoundingClientRect().height;
        const outerFooterContainer = footerRef?.current;
        const footerSectionHeight =
          outerFooterContainer?.getBoundingClientRect().height;

        if (
          !(
            footerSectionHeight &&
            inputAreaHeight &&
            headerSectionHeight &&
            document.body.getBoundingClientRect().height &&
            minimumWidgetHeight
          )
        ) {
          return;
        }

        const widgetHeightIsAtItsMinimum =
          document.body.getBoundingClientRect().height === minimumWidgetHeight;
        const heightDeficitAfterLegalStatementUnmounting =
          minimumWidgetHeight -
          (headerSectionHeight + inputAreaHeight + footerSectionHeight);
        const widgetElementsWillFallBelowHeightMinimum =
          heightDeficitAfterLegalStatementUnmounting >= 0;
        if (
          (widgetHeightIsAtItsMinimum ||
            widgetElementsWillFallBelowHeightMinimum) &&
          !isConsentStatementAboveNavigation
        ) {
          /* The footer now needs to be shifted back down to the bottom of the widget area
           * The vertical distance that the footer needs to get shifted by is the
           * heightDeficitAfterLegalStatementUnmounting. For smoothness, this
           * shift is established via a CSS transition on the inputArea's minHeight.
           */
          const currentMinHeightOfInputArea = window.getComputedStyle(
            inputAreaParentContainer,
            null
          )?.minHeight;
          const newMinHeightOfTheInputArea =
            parseInt(currentMinHeightOfInputArea, 10) +
            heightDeficitAfterLegalStatementUnmounting;
          inputAreaParentContainer.style.transition = `min-height ${widgetResizeDuration}ms ease 0s`;
          updateAndExposeDynamicHeightData({
            requiredInputAreaHeight: undefined,
            shouldResizeInputArea: false,
          });
          inputAreaParentContainer.style.minHeight = `${newMinHeightOfTheInputArea}px`;
        }
      }, 0);
    }
  }, [shouldRenderLegalStatement]);

  useEffect(() => {
    /*
     * The 4 purposes of this handler are:
     *
     * - To reset (at the end of the ENTER phase) the temporary smoothing
     *   transition style that was put in place by a previous useEffect to handle
     *   a bad UX scenario when navigating back from the final form page.
     * - To reset (at the end of the ENTER phase) the shared variable,
     *   isLegalStatementMountingOrUnmounting, back to false and emit the event
     *   isAutoScrollTempDisabled to clarify that autoscroll shall be skipped.
     * - To reset (at the end of the ENTER phase) the main form container's
     *   display style setting back to 'block'. The main form container is
     *   essentially the parent of inputAreaParentContainer and footerContainer
     *   and header. The flex display style is temporary.
     * - To reveal the temporarily transparent footer container via fade-in
     *   effect at the end of the ENTER phase (in order to ensure a smooth UX
     *   when transitioning from a page with a Loader to a page with a legal
     *   consent statement).
     */
    if (!isDynamicHeightEnabled) return;
    if (enterPhase === false) {
      // END of ENTER phase

      const inputAreaDomQueryString = isDynamicHeightExceedingMaximumHeight
        ? "[data-testid='inputAreaContainer']"
        : '#inputAreaParentContainer';
      const inputAreaParentContainer = document.querySelector(
        inputAreaDomQueryString
      );
      if (!inputAreaParentContainer) {
        return;
      }

      /**
       * Some pages require the main form container (i.e. the parent of
       * inputAreaParentContainer) to have a flex display style for optimal UX
       * (e.g. the loader page)
       */
      const isFormAFlexContainer =
        inputAreaParentContainer.parentNode.style.display === 'flex';
      const wasPreviousPageStaticLoader =
        pageNumbersOfStaticLoaders?.includes(previousPage) &&
        previousPage !== undefined;
      /**
       * The following block addresses dynamic height related UX optimisations
       * immediately after the return of the nav bar at the end of the
       * interstitial loader animation.
       */
      if (isFormAFlexContainer && wasPreviousPageStaticLoader) {
        updateAndExposeDynamicHeightData({
          blockNavigation: true,
          timeStampOfBlockNavigationUpdate: Date.now(),
        });
        logToConsole.debug('[DYNAMIC HEIGHT] NAV BLOCKED 1');
        let isInputAreaHeightFixed = false;
        do {
          const { inputAreaRef } =
            window?.__private__?.dynamicWidgetHeightData || {};
          const currentInputAreaHeight = Math.max(
            parseInt(minimumInputAreaHeight, 10),
            inputAreaRef?.current?.getBoundingClientRect().height || 0
          );
          const inputAreaMinHeight = Math.floor(
            parseFloat(inputAreaParentContainer.style.minHeight)
          );
          const inputAreaMaxHeight = Math.floor(
            parseFloat(inputAreaParentContainer.style.maxHeight)
          );
          const isCurrentHeightEqualToMinHeight =
            Math.floor(currentInputAreaHeight) === inputAreaMinHeight;
          const isMaxHeightEqualToMinHeight =
            inputAreaMaxHeight === inputAreaMinHeight;
          isInputAreaHeightFixed =
            isCurrentHeightEqualToMinHeight && isMaxHeightEqualToMinHeight;
          inputAreaParentContainer.style.transition = `height ${widgetResizeDuration}ms ease 0s, min-height ${widgetResizeDuration}ms ease 0s, max-height ${widgetResizeDuration}ms ease 0s`;
          updateAndExposeDynamicHeightData({
            requiredInputAreaHeight: undefined,
            shouldResizeInputArea: false,
          });
          inputAreaParentContainer.style.maxHeight = `${currentInputAreaHeight}px`;
          inputAreaParentContainer.style.minHeight = `${currentInputAreaHeight}px`;
        } while (!isInputAreaHeightFixed);

        setTimeout(() => {
          inputAreaParentContainer.parentNode.style.display = 'block';
          // GOLD-559: Reveal the temporarily transparent footer via fade-in effect
          // eslint-disable-next-line no-shadow
          const { footerRef } =
            window?.__private__?.dynamicWidgetHeightData || {};
          const outerFooterContainer = footerRef?.current;
          if (outerFooterContainer?.style?.opacity === '0') {
            outerFooterContainer.style.transition = 'opacity 1s ease 0s';
            outerFooterContainer.style.opacity = 1;
          }
          updateAndExposeDynamicHeightData({
            blockNavigation: false,
            timeStampOfBlockNavigationUpdate: Date.now(),
          });
          logToConsole.debug('[DYNAMIC HEIGHT] NAV UNBLOCKED 2');
        }, 0);
      }

      updateAndExposeDynamicHeightData({
        isLegalStatementMountingOrUnmounting: false,
      });
      exposeEventToPartner({
        eventType: 'isAutoScrollTempDisabled',
        eventObject: {
          iFrameId,
          skipAutoScroll: false,
        },
      });
    }
  }, [enterPhase]);

  const submissionBlockPages = submissionBlocks.map(
    (submissionBlock) => submissionBlock.page
  );
  const earliestSubmissionBlockPage =
    submissionBlockPages.length > 0
      ? Math.min(...submissionBlockPages)
      : undefined;
  useEffect(() => {
    const pageNumbers = getPageNumbersOfStaticLoaders({
      components,
      earliestSubmissionBlockPage,
    });
    setPageNumbersOfStaticLoaders(pageNumbers);
  }, [earliestSubmissionBlockPage]);

  useEffect(() => {
    if (!isDynamicHeightEnabled) return;
    const wasPreviousPageStaticLoader =
      pageNumbersOfStaticLoaders?.includes(previousPage) &&
      previousPage !== undefined;
    if (!wasPreviousPageStaticLoader && currentPage > 0) {
      if (isWidgetHeightRecalculationRequired) {
        dispatch(
          updateRecalculateRequiredWidgetHeight({
            isWidgetHeightRecalculationRequired: false,
          })
        );
      }
      dispatch(
        updateRecalculateRequiredWidgetHeight({
          isWidgetHeightRecalculationRequired: true,
        })
      );
    }
  }, [currentPage]);

  useEffect(() => {
    if (!isDynamicHeightEnabled) return;
    if (pageNumbersOfStaticLoaders) {
      const isThereAStaticLoaderOnTheCurrentPage =
        pageNumbersOfStaticLoaders?.includes(currentPage);
      setIsCurrentPageStaticLoader(isThereAStaticLoaderOnTheCurrentPage);
      updateAndExposeDynamicHeightData({
        isThereAStaticLoaderOnTheCurrentPage,
      });
    }
  }, [currentPage, pageNumbersOfStaticLoaders]);

  useEffect(() => {
    if (!isDynamicHeightEnabled) return;
    updateAndExposeDynamicHeightData({
      isThereALegalStatementOnTheCurrentPage,
    });
  }, [currentPage]);

  useEffect(() => {
    if (!isDynamicHeightEnabled) return;

    const inputAreaDomQueryString = isDynamicHeightExceedingMaximumHeight
      ? "[data-testid='inputAreaContainer']"
      : '#inputAreaParentContainer';
    const inputAreaParentContainer = document.querySelector(
      inputAreaDomQueryString
    );
    if (!inputAreaParentContainer) return;

    const currentInputAreaHeight = parseFloat(
      inputAreaParentContainer.style.minHeight
    );
    if (
      isSinglePageForm &&
      formInputsSectionHeight !== currentInputAreaHeight
    ) {
      /**
       * Schedule a separate synchronised height resize of the input area
       */
      updateAndExposeDynamicHeightData({
        requiredInputAreaHeight: `${formInputsSectionHeight}px`,
        shouldResizeInputArea: true,
      });
    }
  }, [formInputsSectionHeight]);

  // MAIN SIDE EFFECT OF THIS HOOK - height change logic is here
  useEffect(() => {
    if (!isDynamicHeightEnabled) return;
    const wasPreviousPageStaticLoader =
      pageNumbersOfStaticLoaders?.includes(previousPage) &&
      previousPage !== undefined;
    if (wasPreviousPageStaticLoader) {
      /**
       * This early return allows the main form container (i.e. the parent of
       * inputAreaParentContainer) to complete its UI adjustment from flex to
       * block display style settings to avoid any jumpy height adjustments
       * of the inputArea
       */
      dispatch(
        updateRecalculateRequiredWidgetHeight({
          isWidgetHeightRecalculationRequired: false,
        })
      );
      return;
    }
    if (isWidgetHeightRecalculationRequired) {
      const {
        headerRef,
        footerRef,
        thankYouPageContentRef,
        newsletterSignupContainerRef,
        isLegalStatementMountingOrUnmounting,
        widgetHeightOverrides,
      } = window?.__private__?.dynamicWidgetHeightData || {};

      const currentRequiredWidgetHeight = requiredWidgetHeight;
      const inputAreaDomQueryString = isDynamicHeightExceedingMaximumHeight
        ? "[data-testid='inputAreaContainer']"
        : '#inputAreaParentContainer';
      const inputAreaParentContainer = document.querySelector(
        inputAreaDomQueryString
      );

      let newWidgetHeight;
      let newHeaderSectionHeight;
      let newFormInputsSectionHeight;
      let newFooterSectionHeight;
      const isCurrentPageThankYouPage = thankYouPageContentRef?.current;
      const isCurrentPageNewsLetterSignupPage =
        newsletterSignupContainerRef?.current;

      /**
       * Only on the first render of the page do we want to calculate if the page has just changed height, i.e. if the prev page is not the current page and pageChangeTracker is populated in full
       */
      const isDesktopScreen =
        document.body.getBoundingClientRect().width >= 705;
      if (isCurrentPageThankYouPage) {
        newWidgetHeight =
          thankYouPageContentRef?.current?.getBoundingClientRect().height;
      } else if (isCurrentPageNewsLetterSignupPage) {
        newWidgetHeight =
          newsletterSignupContainerRef?.current?.getBoundingClientRect().height;
      } else if (isCurrentPageStaticLoader) {
        newHeaderSectionHeight =
          headerRef?.current?.getBoundingClientRect().height || 0;
        newFooterSectionHeight =
          footerRef?.current?.getBoundingClientRect().height || 0;
        newWidgetHeight =
          newHeaderSectionHeight +
          newFooterSectionHeight +
          (isLottieAnimationDataAndRendererReady && isDesktopScreen
            ? 490
            : 380);
      } else {
        const transitioningAwayFromTheStaticLoaderPage =
          pageNumbersOfStaticLoaders &&
          pageNumbersOfStaticLoaders.includes(previousPage) &&
          hasPageJustChanged;

        /* transitioningAwayFromTheStaticLoaderPage guard clause conditional prevents a UX glitch where the navBar dynamically increases
         * in height after navigating away from the static loader page. In this case, the
         * navBar height increases before the form inputs section height does. We want an
         * early return that skips this intermittent wrong widget height value from being
         * enforced.
         */
        if (transitioningAwayFromTheStaticLoaderPage) {
          dispatch(
            updateRecalculateRequiredWidgetHeight({
              isWidgetHeightRecalculationRequired: false,
            })
          );
          return;
        }
        newHeaderSectionHeight =
          headerRef?.current?.getBoundingClientRect().height || 0;
        newFooterSectionHeight =
          footerRef?.current?.getBoundingClientRect().height || 0;
        newFormInputsSectionHeight = formInputsSectionHeight;
        const footerMargin = isConsentStatementAboveNavigation ? 0 : 5;
        const newFormInputsSectionHeightExceedsMinimum =
          newFormInputsSectionHeight > parseInt(minimumInputAreaHeight, 10);
        if (enterPhase && newFormInputsSectionHeightExceedsMinimum) {
          /* On form pages that contain a legal statement AND require a page height
           * adjustment during the page transition, this call needs to happen just
           * before the height resize.
           * Chances are that when newFormInputsSectionHeightExceedsMinimum is
           * true, a visible height resize will be needed.
           */
          disableFixedHeightOfInputArea();
        }

        // Set a new widget height in preparation for the resize of the widget
        if (shouldRenderLegalStatement && minimumInputAreaHeight) {
          newWidgetHeight = Math.ceil(
            newHeaderSectionHeight +
              newFooterSectionHeight +
              Math.max(
                parseInt(minimumInputAreaHeight, 10),
                newFormInputsSectionHeight
              ) +
              footerMargin /* may or may not be 5 px in height */
          );
        } else {
          newWidgetHeight = Math.ceil(
            newHeaderSectionHeight +
              newFooterSectionHeight +
              newFormInputsSectionHeight
          );
        }
      }

      const widgetHeightOverride = getWidgetHeightOverride(
        widgetHeightOverrides
      );
      if (widgetHeightOverride && widgetHeightOverride > newWidgetHeight) {
        newWidgetHeight = widgetHeightOverride;
      }

      const isDynamicHeightWithinMaximumHeight =
        newWidgetHeight < maximumWidgetHeight;
      /**
       * What happens if isDynamicHeightWithinMaximumHeight becomes false:
       * Update the main form container (i.e. the parent of
       * inputAreaParentContainer) display style to 'flex' in preparation for
       * the static height flow and the ScrollContainer.
       */
      const isWidgetHeightAboutToExceedMaximumHeight =
        !isDynamicHeightWithinMaximumHeight && // refers to the next widget height that's about to be enforced
        currentRequiredWidgetHeight <= maximumWidgetHeight; // refers to the current widget height that's about to be updated
      const isWidgetHeightAboutToFallBelowMaximumHeight =
        isDynamicHeightWithinMaximumHeight && // refers to the next widget height that's about to be enforced
        currentRequiredWidgetHeight > maximumWidgetHeight; // refers to the current widget height that's about to be updated
      logToConsole.debug(
        '[DYNAMIC HEIGHT] isWidgetHeightAboutToFallBelowMaximumHeight',
        isWidgetHeightAboutToFallBelowMaximumHeight,
        'isDynamicHeightWithinMaximumHeight',
        isDynamicHeightWithinMaximumHeight,
        'currentRequiredWidgetHeight',
        currentRequiredWidgetHeight
      );

      // UX: Fade out the footer to fade it back in after the DOM transition from dynamic height to static height container is complete
      const outerFooterContainer = footerRef?.current;
      if (isWidgetHeightAboutToExceedMaximumHeight) {
        // Fade out
        outerFooterContainer.style.transition = 'opacity 100ms ease 0s';
        outerFooterContainer.style.opacity = 0;
      }
      if (
        !isWidgetHeightAboutToExceedMaximumHeight &&
        (!isDynamicHeightWithinMaximumHeight ||
          window.__private__?.isEnterAnimationInProgress ||
          enterPhase)
      ) {
        if (outerFooterContainer?.style?.opacity === '0') {
          // Fade in
          outerFooterContainer.style.transition = 'opacity 1s ease 0s';
          outerFooterContainer.style.opacity = 1;
        }
      }

      if (!enterPhase && isWidgetHeightAboutToExceedMaximumHeight) {
        if (inputAreaParentContainer?.parentNode) {
          updateAndExposeDynamicHeightData({
            requiredInputAreaHeight: undefined,
            shouldResizeInputArea: false,
          });
          do {
            inputAreaParentContainer.style.minHeight = 'auto';
            inputAreaParentContainer.parentNode.style.display = 'flex';
          } while (
            window.getComputedStyle(inputAreaParentContainer.parentNode)
              ?.display !== 'flex'
          );
        }
      }
      if (
        (enterPhase || exitPhase) &&
        (isWidgetHeightAboutToExceedMaximumHeight ||
          isWidgetHeightAboutToFallBelowMaximumHeight)
      ) {
        /**
         * The ENTER phase is a delicate time in the form flow, where the
         * <Transition /> component and its helper transition the new page into
         * the widget's viewport. Should either of these two booleans be true
         * at this time
         * - isWidgetHeightAboutToExceedMaximumHeight
         * - isWidgetHeightAboutToFallBelowMaximumHeight
         * ... the <Transition /> component will be unmounted and remounted
         * under either a ScrollContainer or a non-scrollable dynamic div. This
         * would abruptly abort the ENTER animation and instead make the new form
         * page appear unpleasantly.
         * To prevent that, we postpone the widget resize until the end of
         * the ENTER phase.
         */
        updateAndExposeDynamicHeightData({ shouldPostponeWidgetResize: true }); // to be picked up again after the end of the ENTER phase
        newWidgetHeight = currentRequiredWidgetHeight; // Maintain the existing widget height for now and don't trigger a widget resize
      }

      /**
       * Input area height management
       */
      const { shouldResizeInputArea } =
        window?.__private__?.dynamicWidgetHeightData || {};
      const isPostConversionPage =
        isCurrentPageThankYouPage || isCurrentPageNewsLetterSignupPage;
      logToConsole.debug(
        '[DYNAMIC HEIGHT] Input area height management - Before main guard'
      );
      const updateResizeTransitionTracker = (eventType, newState) => {
        setIsInputAreaResizeInProgress(newState);
        if (eventType === 'transitionstart') {
          inputAreaParentContainer.removeEventListener(
            eventType,
            resizeTransitionStartEventListener
          );
        }
        if (eventType === 'transitionend') {
          inputAreaParentContainer.removeEventListener(
            eventType,
            resizeTransitionEndEventListener
          );
        }
      };
      // The listeners below manage the inputAreaParentContainer's height transitions
      const resizeTransitionStartEventListener = () =>
        updateResizeTransitionTracker('transitionstart', true);
      const resizeTransitionEndEventListener = () =>
        updateResizeTransitionTracker('transitionend', false);

      const mainGuardOfInputAreaHeightManagement =
        !isLegalStatementMountingOrUnmounting &&
        !isCurrentPageStaticLoader &&
        !isPostConversionPage &&
        inputAreaParentContainer?.parentNode?.style.display === 'block';
      logToConsole.debug(
        '[DYNAMIC HEIGHT] Input area height management - Main guard',
        mainGuardOfInputAreaHeightManagement
      );
      if (!mainGuardOfInputAreaHeightManagement) {
        logToConsole.debug(
          '[DYNAMIC HEIGHT] Input area height management: Reason for mainGuard = false',
          !isLegalStatementMountingOrUnmounting
            ? ''
            : '!isLegalStatementMountingOrUnmounting',
          !isCurrentPageStaticLoader ? '' : '!isCurrentPageStaticLoader',
          !isLegalStatementMountingOrUnmounting
            ? ''
            : '!isLegalStatementMountingOrUnmounting',
          inputAreaParentContainer?.parentNode?.style.display === 'block'
            ? ''
            : "inputAreaParentContainer?.parentNode?.style.display === 'block'"
        );
      }

      if (mainGuardOfInputAreaHeightManagement) {
        inputAreaParentContainer.style.transition = `min-height ${widgetResizeDuration}ms ease 0s`;
        let newInputAreaHeight = Math.ceil(newFormInputsSectionHeight);
        newHeaderSectionHeight =
          headerRef?.current?.getBoundingClientRect().height || 0;
        newFooterSectionHeight =
          footerRef?.current?.getBoundingClientRect().height || 0;
        if (newWidgetHeight <= minimumWidgetHeight) {
          newInputAreaHeight =
            minimumWidgetHeight -
            (newHeaderSectionHeight + newFooterSectionHeight);
        }
        if (newInputAreaHeight < parseInt(minimumInputAreaHeight, 10)) {
          newInputAreaHeight = parseInt(minimumInputAreaHeight, 10);
        }
        const firstGuard =
          inputAreaParentContainer.style.minHeight !==
          `${newInputAreaHeight}px`;
        logToConsole.debug(
          '[DYNAMIC HEIGHT] Input area height management - 1st guard within main',
          ' block',
          firstGuard,
          firstGuard
            ? ''
            : `REASON: ${
                inputAreaParentContainer.style.minHeight !==
                `${newInputAreaHeight}px`
                  ? ''
                  : `inputAreaParentContainer.style.minHeight !== ${newInputAreaHeight}px`
              } and/or ${
                !isInputAreaResizeInProgress
                  ? ''
                  : '!isInputAreaResizeInProgress'
              }`
        );

        if (
          inputAreaParentContainer.style.minHeight !== `${newInputAreaHeight}px`
        ) {
          inputAreaParentContainer.style.maxHeight = 'none';
          inputAreaParentContainer.addEventListener(
            'transitionstart',
            resizeTransitionStartEventListener
          );
          inputAreaParentContainer.addEventListener(
            'transitionend',
            resizeTransitionEndEventListener
          );
          if (
            inputAreaParentContainer.style.transition ===
              `height ${widgetResizeDuration}ms ease 0s, min-height ${widgetResizeDuration}ms ease 0s, max-height ${widgetResizeDuration}ms ease 0s` ||
            inputAreaParentContainer.style.transition ===
              `height ${widgetResizeDuration}ms ease 0s, min-height ${widgetResizeDuration}ms ease 0s, max-height ${widgetResizeDuration}ms ease 0s`
          ) {
            logToConsole.debug(
              '[DYNAMIC HEIGHT] Input area height management - Case 1',
              `Fixing input area height to ${newInputAreaHeight}px`
            );
            updateAndExposeDynamicHeightData({
              requiredInputAreaHeight: undefined,
              shouldResizeInputArea: false,
            });
            inputAreaParentContainer.style.minHeight = 'none';
            inputAreaParentContainer.style.height = `${newInputAreaHeight}px`;
            inputAreaParentContainer.style.maxHeight = 'none';
          } else {
            const hasInitialLoadJustFinished =
              previousPage === undefined && haveAllInitialMessagesArrived;
            logToConsole.debug(
              '[DYNAMIC HEIGHT] Input area height management - Guard before Case 2',
              'hasInitialLoadJustFinished && !hasInputAreaBeenResized',
              hasInitialLoadJustFinished && !hasInputAreaBeenResized,
              hasInitialLoadJustFinished && !hasInputAreaBeenResized
                ? ''
                : `REASON: ${
                    hasInitialLoadJustFinished
                      ? ''
                      : 'hasInitialLoadJustFinished'
                  } and/or ${
                    !hasInputAreaBeenResized ? '' : '!hasInputAreaBeenResized'
                  }`
            );

            if (hasInitialLoadJustFinished && !hasInputAreaBeenResized) {
              logToConsole.debug(
                '[DYNAMIC HEIGHT] Input area height management - Case 2',
                'Inititate an immediate height resize of the input area to: ',
                `${newInputAreaHeight}px`
              );
              /**
               * Inititate an immediate height resize of the input area
               */
              updateAndExposeDynamicHeightData({
                requiredInputAreaHeight: undefined,
                shouldResizeInputArea: false,
              });
              inputAreaParentContainer.style.minHeight = `${newInputAreaHeight}px`;
            } else if (shouldResizeInputArea) {
              logToConsole.debug(
                '[DYNAMIC HEIGHT] Input area height management - Case 3',
                'Schedule a separate delayed height resize of the input area',
                `to: ${newInputAreaHeight}px`
              );
              /**
               * Fix the input area to an exact value to decouple it from widget
               * resizes. This achieves a UX that resembles an accordion / drawer
               * because sections such as the footer seem to resize independently.
               */
              updateAndExposeDynamicHeightData({
                requiredInputAreaHeight: undefined,
                shouldResizeInputArea: false,
              });
              inputAreaParentContainer.style.minHeight = `${newInputAreaHeight}px`;
              inputAreaParentContainer.style.maxHeight = `${newInputAreaHeight}px`;
            } else {
              logToConsole.debug(
                '[DYNAMIC HEIGHT] Input area height management - Case 4',
                'Schedule a separate delayed height resize of the input area',
                `to: ${newInputAreaHeight}px`
              );
              /**
               * Schedule a separate synchronised height resize of the input area
               */
              updateAndExposeDynamicHeightData({
                requiredInputAreaHeight: `${newInputAreaHeight}px`,
                shouldResizeInputArea: true,
              });
            }
          }
        }
      }

      if (newWidgetHeight < minimumWidgetHeight) {
        newWidgetHeight = minimumWidgetHeight;
        if (requiredWidgetHeight === minimumWidgetHeight) {
          inputAreaParentContainer?.removeEventListener(
            'transitionstart',
            resizeTransitionStartEventListener
          );
          inputAreaParentContainer?.removeEventListener(
            'transitionend',
            resizeTransitionEndEventListener
          );
        }
      }
      // Grow-only: raise a would-be shrink to the session high-water mark so the
      // change-detection guard below suppresses it, stopping the failsafe poller
      // re-posting resizeWidget every tick.
      newWidgetHeight = getGrowOnlyClampedHeight(newWidgetHeight);
      // While a flag change is awaiting its replacement legal statement, hold
      // the height rather than shrinking on the transiently null statement.
      // This stops the applied iframe height diverging from the stored height
      // and leaving the consent statement clipped.
      newWidgetHeight = holdWidgetHeightDuringFlagRefetch(
        newWidgetHeight,
        currentRequiredWidgetHeight,
        isAwaitingFlagDrivenRefetchRef.current
      );
      const skipAutoScroll = isLegalStatementMountingOrUnmounting;
      if (currentRequiredWidgetHeight !== newWidgetHeight) {
        if (isAutoScrollEnabled && hasPageJustChanged && !skipAutoScroll) {
          handleAutoScroll(
            newWidgetHeight,
            iFrameId,
            requiredWidgetHeight,
            minimumWidgetHeight,
            maximumWidgetHeight,
            currentPage,
            widgetResizeDuration
          );
        }

        /**
         * Input area height management
         */
        if (isLegalStatementMountingOrUnmounting && enterPhase) {
          const widgetHeightDelta =
            newWidgetHeight - currentRequiredWidgetHeight;
          let requiredInputAreaHeight =
            parseFloat(inputAreaParentContainer.style.minHeight) +
            widgetHeightDelta;
          if (
            typeof requiredInputAreaHeight === 'number' &&
            !Number.isNaN(requiredInputAreaHeight)
          ) {
            if (
              requiredInputAreaHeight < parseInt(minimumInputAreaHeight, 10)
            ) {
              requiredInputAreaHeight = parseInt(minimumInputAreaHeight, 10);
            }
            if (requiredInputAreaHeight < formInputsSectionHeight) {
              requiredInputAreaHeight = formInputsSectionHeight;
            }
            /**
             * Schedule a separate synchronised height resize of the input area
             */
            logToConsole.debug(
              '[DYNAMIC HEIGHT] Input area height management - Case 5',
              'isLegalStatementMountingOrUnmounting && enterPhase',
              'Schedule a separate delayed height resize of the input area to:',
              ` ${requiredInputAreaHeight}px`
            );
            updateAndExposeDynamicHeightData({
              requiredInputAreaHeight: `${requiredInputAreaHeight}px`,
              shouldResizeInputArea: true,
            });
          }
        }

        if (isDynamicHeightWithinMaximumHeight) {
          dispatch(
            updateRequiredWidgetHeight({
              requiredWidgetHeight: newWidgetHeight,
              iFrameId,
              widgetResizeDuration,
              showAppointmentBooking,
              showCpcRecommendations,
              isBounceNavigationAnimationEnabled,
            })
          );
        } else {
          setTimeout(() => {
            dispatch(
              updateRequiredWidgetHeight({
                requiredWidgetHeight: newWidgetHeight,
                iFrameId,
                widgetResizeDuration,
                showCpcRecommendations,
                showAppointmentBooking,
                isBounceNavigationAnimationEnabled,
              })
            );
          }, gridDisplayStyleAdjustmentDelay);
        }
      }

      /**
       * isWidgetHeightRecalculationRequired toggles to true whenever the
       * required widget height needs to be re-examined and possibly updated,
       * Here we set it back to false to prevent an infinite loop of height
       * recalculations.
       */
      dispatch(
        updateRecalculateRequiredWidgetHeight({
          isWidgetHeightRecalculationRequired: false,
        })
      );
    } else {
      const { isComplexWidgetResizeInProgress } =
        window?.__private__?.dynamicWidgetHeightData || {};
      if (isComplexWidgetResizeInProgress && !exitPhase && !enterPhase) {
        setTimeout(() => {
          updateAndExposeDynamicHeightData({
            isComplexWidgetResizeInProgress: false,
          });
        }, 0);
      }
    }
  }, [isWidgetHeightRecalculationRequired]);

  useEffect(() => {
    if (!isDynamicHeightEnabled) return;

    const { headerRef, inputAreaRef, footerRef } =
      window?.__private__?.dynamicWidgetHeightData || {};

    if (
      headerRef?.current &&
      inputAreaRef?.current &&
      footerRef?.current &&
      isDynamicHeightEnabled &&
      maximumWidgetHeight &&
      minimumWidgetHeight &&
      (!fontOverride || fontLoadedState)
    ) {
      setFormInputsSectionHeight(determineRequiredHeightOfFormInputsSection());
    }
  }, [
    isDynamicHeightEnabled,
    maximumWidgetHeight,
    minimumWidgetHeight,
    fontOverride,
  ]);

  useEffect(() => {
    if (isInputAreaResizeInProgress && !hasInputAreaBeenResized) {
      setHasInputAreaBeenResized(true);
    }
  }, [isInputAreaResizeInProgress]);

  return null;
};

export default DynamicWidgetHeight;
